import { derived } from 'svelte/store';
import { termsStore } from './termsStore.js';
import { claimsStore } from './editorsStore.js';

/**
 * Sort indexed term entries by their first appearance in claims
 * @param {Array} termEntries - Array of [indexedKey, termData] pairs
 * @param {string} _claimsContent - Claims content to search in
 * @returns {Array} - Sorted array of [indexedKey, termData] pairs
 */
function sortTermEntriesByFirstAppearance(termEntries, _claimsContent) {
    // Sort by claim number first, then by start position within the claim
    // BUT: terms with needs_refresh go to the end of their claim group
    const sortedEntries = [...termEntries].sort((a, b) => {
        const termDataA = a[1];
        const termDataB = b[1];
        
        // Get claim numbers (default to high number if not found)
        const claimNumA = termDataA.claim_num ?? 9999;
        const claimNumB = termDataB.claim_num ?? 9999;
        
        // Sort by claim number first
        if (claimNumA !== claimNumB) {
            return claimNumA - claimNumB;
        }
        
        // Within same claim, terms with needs_refresh go to the end
        const needsRefreshA = termDataA.needs_refresh || false;
        const needsRefreshB = termDataB.needs_refresh || false;
        
        if (needsRefreshA !== needsRefreshB) {
            return needsRefreshA ? 1 : -1; // needs_refresh terms go after normal terms
        }
        
        // Within same claim and same refresh status, sort by start position
        const startCharA = termDataA.start_char ?? 0;
        const startCharB = termDataB.start_char ?? 0;
        
        if (startCharA !== startCharB) {
            return startCharA - startCharB;
        }
        
        // Use occurrence order as final tiebreaker
        const orderA = termDataA.occurrence_order ?? 0;
        const orderB = termDataB.occurrence_order ?? 0;
        return orderA - orderB;
    });

    return sortedEntries;
}

/**
 * Sort indexed term entries alphabetically
 * @param {Array} termEntries - Array of [indexedKey, termData] pairs
 * @returns {Array} - Alphabetically sorted array of [indexedKey, termData] pairs
 */
function sortTermEntriesAlphabetically(termEntries) {
    return [...termEntries].sort((a, b) => {
        const termA = a[1].parent_term.toLowerCase();
        const termB = b[1].parent_term.toLowerCase();
        
        // First sort by term text, then by indexed key for same terms
        if (termA !== termB) {
            return termA.localeCompare(termB);
        }
        return a[0].localeCompare(b[0]); // Sort by indexed key
    });
}

/**
 * Reactive derived store for sorted indexed term entries
 * Automatically updates when term entries or claims content changes
 * This eliminates the need for manual sorting in components
 */
export const sortedTerms = derived(
    [termsStore, claimsStore],
    ([$termsStore, $claimsStore]) => {
        const termEntries = Array.from($termsStore.termEntries.entries());
        const sortMode = $termsStore.sortMode || 'appearance';
        
        if (sortMode === 'alphabetical') {
            return sortTermEntriesAlphabetically(termEntries);
        } else {
            return sortTermEntriesByFirstAppearance(termEntries, $claimsStore.content);
        }
    }
);

/**
 * Check if a term has any errors (article errors or parent term warnings)
 * Duplicated from TermsPanel for use in filtering
 * @param {Object} termData - Term data object from the store
 * @returns {boolean} true if the term has errors
 */
function hasErrors(termData) {
    // Check for parent term warnings
    if (termData._warning && termData._warning.type === 'prefix_warning') {
        return true;
    }
    
    // Check for article errors in child terms
    if (termData.children) {
        for (const [_childTerm, childData] of Object.entries(termData.children)) {
            if (childData.references && childData.references.errors) {
                // Check if any error exists in the errors array
                const hasArticleError = childData.references.errors.some(error => 
                    error && (error.type === 'missing_article' || error.type === 'incorrect_article')
                );
                if (hasArticleError) {
                    return true;
                }
            }
        }
    }
    
    return false;
}

/**
 * Reactive derived store for filtered terms based on view filter
 * Applies the selected filter (all, errors, selected, focus) to the sorted terms
 */
export const filteredTerms = derived(
    [sortedTerms, termsStore],
    ([$sortedTerms, $termsStore]) => {
        const { viewFilter, selectedEntries, focusedClaimNumber } = $termsStore;

        switch(viewFilter) {
            case 'errors': {
                return $sortedTerms.filter(([_key, data]) => hasErrors(data));
            }

            case 'selected': {
                return $sortedTerms.filter(([key]) =>
                    selectedEntries && selectedEntries.has(key)
                );
            }

            case 'focus': {
                if (focusedClaimNumber === null) {
                    return $sortedTerms;
                }
                return $sortedTerms.filter(([_key, data]) =>
                    data.claim_num === focusedClaimNumber
                );
            }

            case 'all':
            default:
                return $sortedTerms;
        }
    }
);